import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type { PostFormat } from '~/features/crossposting/lib/formats'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'

const THIRDS = [33.333, 66.666]
const KEY_STEP = 0.02

interface Props {
  previewUrl: string
  format: PostFormat
  offset: number
  onOffsetChange: (offset: number) => void
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * Previews the section a platform keeps. The frame holds the target aspect and
 * fills the image on its tight axis; only the slack axis can be dragged, which
 * is why a single 0..1 offset describes the whole interaction.
 */
export function ImageCropFrame({ previewUrl, format, offset, onOffsetChange }: Props) {
  const containerRef = useRef<HTMLButtonElement>(null)
  const dragStart = useRef<{ pointerId: number; position: number; offset: number } | null>(null)
  const hintId = useId()
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setFailed(false)
    setNatural(null)
    const image = new Image()
    image.onload = () => {
      if (!cancelled) setNatural({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      if (!cancelled) setFailed(true)
    }
    image.src = previewUrl
    return () => {
      cancelled = true
    }
  }, [previewUrl])

  if (failed) {
    return (
      <p className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-destructive text-sm">
        {m.crosspostingImageUnprocessable()}
      </p>
    )
  }

  if (!natural) {
    return <div className="mx-auto h-48 w-full max-w-xs animate-pulse rounded-md bg-muted" />
  }

  const targetAspect = format.width / format.height
  const sourceAspect = natural.width / natural.height
  const horizontal = sourceAspect > targetAspect
  const frameFraction = horizontal ? targetAspect / sourceAspect : sourceAspect / targetAspect
  const slack = 1 - frameFraction
  const draggable = slack > 0.001

  const handlePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = dragStart.current
    // Measure only once a drag is actually in flight - reading the rect on every
    // hover move would force a synchronous layout.
    if (!start || start.pointerId !== event.pointerId) return
    // The capture can be lost without a pointerup ever arriving (a context menu
    // mid-drag does it), which would otherwise leave the frame following the
    // bare cursor.
    if (event.buttons === 0 && event.pointerType === 'mouse') {
      dragStart.current = null
      return
    }
    const box = containerRef.current?.getBoundingClientRect()
    if (!box) return
    const size = horizontal ? box.width : box.height
    if (size === 0) return
    const travelled = (horizontal ? event.clientX : event.clientY) - start.position
    onOffsetChange(clamp(start.offset + travelled / size / slack))
  }

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    // A second finger must not re-anchor the drag the first one is steering.
    if (!draggable || dragStart.current) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragStart.current = {
      pointerId: event.pointerId,
      position: horizontal ? event.clientX : event.clientY,
      offset,
    }
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragStart.current?.pointerId !== event.pointerId) return
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    dragStart.current = null
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!draggable) return
    const back = horizontal ? 'ArrowLeft' : 'ArrowUp'
    const forward = horizontal ? 'ArrowRight' : 'ArrowDown'
    if (event.key !== back && event.key !== forward) return
    event.preventDefault()
    onOffsetChange(clamp(offset + (event.key === forward ? KEY_STEP : -KEY_STEP)))
  }

  const framePercent = `${frameFraction * 100}%`
  const offsetPercent = `${offset * slack * 100}%`

  return (
    <div className="space-y-2">
      <button
        ref={containerRef}
        type="button"
        aria-label={m.crosspostingFramingLabel()}
        aria-describedby={hintId}
        // Height-capped so a tall source cannot grow the card to the full viewport;
        // the source aspect then drives the width, keeping the scrim geometry exact.
        style={{
          aspectRatio: `${natural.width} / ${natural.height}`,
          width: `min(100%, calc(min(18rem, 40vh) * ${sourceAspect}))`,
        }}
        className={cn(
          'relative mx-auto block select-none touch-none overflow-hidden rounded-md border bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring',
          draggable && 'cursor-grab active:cursor-grabbing',
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onLostPointerCapture={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        <img src={previewUrl} alt="" className="h-full w-full object-contain" draggable={false} />

        <div
          className="pointer-events-none absolute border-2 border-background shadow-[0_0_0_9999px] shadow-black/60"
          style={
            horizontal
              ? { left: offsetPercent, top: 0, width: framePercent, height: '100%' }
              : { left: 0, top: offsetPercent, width: '100%', height: framePercent }
          }
        >
          {THIRDS.map((position) => (
            <div
              key={`v-${position}`}
              className="absolute top-0 bottom-0 w-px bg-background/40"
              style={{ left: `${position}%` }}
            />
          ))}
          {THIRDS.map((position) => (
            <div
              key={`h-${position}`}
              className="absolute right-0 left-0 h-px bg-background/40"
              style={{ top: `${position}%` }}
            />
          ))}
        </div>
      </button>

      <p id={hintId} className="text-muted-foreground text-xs">
        {draggable
          ? m.crosspostingFramingHint({
              width: String(format.width),
              height: String(format.height),
            })
          : m.crosspostingFramingExact({
              width: String(format.width),
              height: String(format.height),
            })}
      </p>
    </div>
  )
}
